import { ChevronDown, ChevronRight, Settings2 } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { ActiveLocation } from './model/active-location';
import { sectionItems } from './model/resolve-navigation';

/**
 * The active module's sections as a scrolling tab row — shown ONLY below 900px of shell width,
 * where the rail has left the flow.
 *
 * This is not a second navigation. It is the SAME sections the rail's level two shows, rendered in
 * the one band where the rail is not on screen — the same dual-render convention the rail and the
 * drawer already use, and the standard's own rule that "a visible control must work at every
 * width" read from the other direction: below 900px, reaching another view of the module you are
 * in would otherwise cost opening the drawer, finding the module, and tapping the section. Three
 * taps for what the desktop does in one, on the module the CEO uses from a phone.
 *
 * It is deliberately NOT shown above 900px. There the rail carries the sections a few pixels away,
 * and a tab row repeating them would be the guidelines' "nothing named twice on the same screen"
 * with the two copies visible at once. `shell.css` owns that decision, in the same block that takes
 * the rail out of the flow, so the two can only ever change together.
 *
 * It replaced `FinanceSectionLayout`'s own tab bar, which hardcoded Finance's five views a second
 * time. Every module with sections gets this for free now, and no module declares its sections
 * twice.
 *
 * `Link`, not `NavLink`, for the reason `ModuleRail` records: the active section comes from the
 * model, and `NavLink` would overwrite it with its own path match.
 */
export function SectionTabs({ active }: { active: ActiveLocation }) {
  const { t } = useTranslation('shell');
  const subrowId = useId();
  const currentId = active.section?.id;
  // A toggle holds only for the screen it was made on: moving to another screen gives the group
  // back its own rule (open on its own screens, closed elsewhere), as the rail does on remount.
  const [toggled, setToggled] = useState<{ at: string | undefined; open: boolean } | null>(null);
  const sections = active.module?.sections ?? [];

  if (sections.length === 0) {
    return null;
  }

  const items = sectionItems(sections);
  // Decision 249: a group opens and closes in the menu, and below 900px this row IS the menu. So
  // the group is a toggle, not a link: it shows its entries on a second, quieter row in place,
  // and it is open by itself on one of its own screens. Only one group exists today; the first
  // one is the one this row discloses.
  const group = items.find((item) => item.kind === 'group');
  const holdsCurrent =
    group?.kind === 'group' && group.sections.some((section) => section.id === currentId);
  const open = toggled !== null && toggled.at === currentId ? toggled.open : holdsCurrent;
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <>
      <nav className="section-tabs" aria-label={t('nav.sections')}>
        {items.map((item) =>
          item.kind === 'section' ? (
            <Link
              key={item.section.id}
              to={item.section.path}
              className="section-tab"
              aria-current={currentId === item.section.id ? 'page' : undefined}
            >
              {item.section.label}
            </Link>
          ) : item === group ? (
            <button
              key={`group:${item.id}`}
              type="button"
              className="section-tab section-tab-group"
              aria-expanded={open}
              aria-controls={subrowId}
              aria-current={holdsCurrent ? 'true' : undefined}
              onClick={() => setToggled({ at: currentId, open: !open })}
            >
              <Settings2 className="section-tab-group-icon" aria-hidden="true" />
              <span>{item.label}</span>
              <Chevron className="section-tab-group-chevron" aria-hidden="true" />
            </button>
          ) : null,
        )}
      </nav>
      {group?.kind === 'group' ? (
        <nav
          id={subrowId}
          className="section-tabs section-subtabs"
          aria-label={group.label}
          hidden={!open}
        >
          {group.sections.map((section) => (
            <Link
              key={section.id}
              to={section.path}
              className="section-tab"
              aria-current={currentId === section.id ? 'page' : undefined}
            >
              {section.label}
            </Link>
          ))}
        </nav>
      ) : null}
    </>
  );
}
